import type { BlockEventType, BusEvent, PayloadOf, Unsubscribe } from './types';

type Handler = (event: BusEvent) => void;

interface Subscription {
  pattern: string;
  handler: Handler;
}

export class EventBus {
  private subscriptions: Subscription[] = [];
  private disposed = false;

  publish<T extends BlockEventType>(
    event: T,
    sourceBlock: string,
    payload: PayloadOf<T>,
  ): void {
    if (this.disposed) {
      if (import.meta.env.DEV) {
        console.warn('[block-events] Publish on disposed bus ignored:', event, sourceBlock);
      }
      return;
    }

    const busEvent: BusEvent<T> = {
      type: event,
      sourceBlock,
      payload,
      timestamp: Date.now(),
    };

    if (import.meta.env.DEV) {
      console.debug(`[block-events] PUBLISH ${sourceBlock}:${event}`, payload);
    }

    const matching = this.subscriptions.filter((sub) =>
      this.matchPattern(sub.pattern, sourceBlock, event),
    );

    for (const sub of matching) {
      try {
        sub.handler(busEvent as BusEvent);
      } catch (err) {
        console.error('[block-events] Handler error:', err);
      }
    }
  }

  subscribe(pattern: string, handler: Handler): Unsubscribe {
    if (this.disposed) {
      if (import.meta.env.DEV) {
        console.warn('[block-events] Subscribe on disposed bus ignored:', pattern);
      }
      return () => {};
    }

    const sub: Subscription = { pattern, handler };
    this.subscriptions.push(sub);

    return () => {
      const idx = this.subscriptions.indexOf(sub);
      if (idx !== -1) this.subscriptions.splice(idx, 1);
    };
  }

  subscribeOnce(pattern: string, handler: Handler): Unsubscribe {
    const unsub = this.subscribe(pattern, (event) => {
      unsub();
      handler(event);
    });
    return unsub;
  }

  dispose(): void {
    this.disposed = true;
    this.subscriptions = [];
  }

  private matchPattern(
    pattern: string,
    sourceBlock: string,
    event: string,
  ): boolean {
    const [patternBlock, patternEvent] = pattern.split(':');

    const blockMatch = patternBlock === '*' || patternBlock === sourceBlock;
    const eventMatch = patternEvent === '*' || patternEvent === event;

    return blockMatch && eventMatch;
  }
}
